import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Imports framework (ADR-0036) - generic XLSX-to-entity pipeline shared
 * between API and admin. Module-agnostic at this layer: the row's
 * `rawData` / `fixedData` / `resolvedPayload` are typed `unknown`; each
 * `Importer` on the API side validates its own shape.
 */

export const ImportJobStatusSchema = z.enum([
  'VALIDATING',
  'READY',
  'PARTIAL',
  'COMMITTING',
  'COMPLETED',
  'FAILED',
]);
export type ImportJobStatus = z.infer<typeof ImportJobStatusSchema>;

export const ImportRowStatusSchema = z.enum([
  'INVALID',
  'VALID',
  'SKIPPED',
  'COMMITTED',
  'COMMIT_FAILED',
]);
export type ImportRowStatus = z.infer<typeof ImportRowStatusSchema>;

/**
 * `moduleKey` is open-string today (the Importer registry on the API side
 * is the source of truth), but we type it loosely so the client can render
 * "Leads" / "Customers" labels generically. The known set grows by
 * registering a new Importer; this schema doesn't gatekeep so the API can
 * roll out new modules without a shared-schema release.
 */
export const ImportModuleKeySchema = z.string().min(1).max(40);
export type ImportModuleKey = z.infer<typeof ImportModuleKeySchema>;

/**
 * A column descriptor - what the UI renders on the upload preview header
 * and how the framework matches sheet columns to internal field names.
 * `aliases` lets us tolerate the small variations between legacy
 * spreadsheets (e.g. `Preferred Contact#` vs `Preferred Contact Number`).
 */
/**
 * Master kind backing a server-side lookup column. The admin UI maps
 * each kind to a search hook against an existing master endpoint -
 * we do not invent new endpoints. Keep this list in sync with
 * `ImportLookupCell` in apps/admin.
 */
export const ImportLookupKindSchema = z.enum([
  'city',
  'user',
  'queryType',
  'querySource',
  'queryMedium',
  // ADR-0036 v2 / hotels rollout - new master kinds for multi-sheet imports.
  'country',
  'currency',
  'mealPlan',
  'roomType',
  'occupancyType',
  'supplier',
  // Added with the Activities importer.
  'language',
  /// Lead importer: `destinationCityId` FK targets `destinations`.
  'destination',
  /// Transfer importer: vehicle type master. Previously piggy-backed
  /// `roomType` because the kind didn't exist; promoted to its own kind
  /// so the admin lookup-cell + Excel dropdown both hit the right master.
  'vehicleType',
]);
export type ImportLookupKind = z.infer<typeof ImportLookupKindSchema>;

export const ImportColumnDescriptorSchema = z.object({
  field: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean().optional(),
  /** Hint shown under the field in the inline fix UI. */
  hint: z.string().nullable().optional(),
  /** Alternative header strings accepted from sheets. Case-insensitive. */
  aliases: z.array(z.string()).optional(),
  /**
   * Editor type - drives which inline control the operator gets when
   * fixing a bad cell.
   *   `select` carries its allowed values inline via `options` (small,
   *           fixed enums like Yes/No or 1..5 star).
   *   `lookup` defers option-loading: the UI mounts a debounced
   *           search picker that hits the master endpoint on demand
   *           (used for cities / users / query masters - so the
   *           module descriptor response stays small).
   */
  type: z.enum(['string', 'number', 'date', 'boolean', 'select', 'lookup']).optional(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .nullable()
    .optional(),
  /**
   * Required when `type === 'lookup'`. Names the master the UI should
   * search against. Server populates this from the importer's column
   * descriptor; nothing else carries it on the wire.
   */
  lookup: ImportLookupKindSchema.optional(),
});
export type ImportColumnDescriptor = z.infer<typeof ImportColumnDescriptorSchema>;

/**
 * Field-level validation error emitted by an Importer's row validator.
 * `column` is the descriptor's `field` (machine name), `value` is what the
 * operator saw / typed so the UI can echo it back.
 */
export const ImportFieldErrorSchema = z.object({
  column: z.string(),
  message: z.string(),
  value: z.unknown().nullable().optional(),
});
export type ImportFieldError = z.infer<typeof ImportFieldErrorSchema>;

/**
 * Sheet descriptor for multi-sheet importers (ADR-0036 v2). Single-sheet
 * importers leave `sheets` empty and use the legacy `columns` field for
 * the lone-sheet shape.
 *
 * `key` doubles as the sheet name the framework matches against the XLSX
 * (`workbook.SheetNames`) AND as the identifier persisted on
 * `import_rows.sheet_key`. Keep it short, machine-friendly, and stable
 * across template revisions - operators can re-order the actual sheets
 * in their workbook without breaking the import.
 */
export const ImportSheetDescriptorSchema = z.object({
  key: z.string().min(1).max(40),
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  required: z.boolean().optional(),
  columns: z.array(ImportColumnDescriptorSchema),
});
export type ImportSheetDescriptor = z.infer<typeof ImportSheetDescriptorSchema>;

/**
 * Importer descriptor - the API returns this when the client asks
 * `GET /imports/modules` so the upload wizard can render the column header
 * AND let the operator pick the right importer per moduleKey.
 *
 * `columns` is the legacy single-sheet shape (still used by Leads).
 * `sheets` is the v2 multi-sheet shape (used by Hotels and onwards). When
 * `sheets` is non-empty, clients should ignore `columns` and render one
 * tab per sheet instead.
 */
export const ImportModuleDescriptorSchema = z.object({
  moduleKey: ImportModuleKeySchema,
  displayName: z.string(),
  description: z.string().nullable().optional(),
  columns: z.array(ImportColumnDescriptorSchema),
  sheets: z.array(ImportSheetDescriptorSchema).optional(),
});
export type ImportModuleDescriptor = z.infer<typeof ImportModuleDescriptorSchema>;

// ---------------------------------------------------------------------------
// Job + Row response shapes
// ---------------------------------------------------------------------------

export const ImportJobListItemSchema = z.object({
  id: UuidSchema,
  moduleKey: ImportModuleKeySchema,
  fileName: z.string(),
  fileSizeBytes: z.number().int(),
  totalRows: z.number().int(),
  validRows: z.number().int(),
  invalidRows: z.number().int(),
  skippedRows: z.number().int(),
  committedRows: z.number().int(),
  failedRows: z.number().int(),
  status: ImportJobStatusSchema,
  errorMessage: z.string().nullable(),
  createdById: UuidSchema,
  createdByName: z.string().nullable(),
  startedAt: IsoDateTimeSchema,
  validatedAt: IsoDateTimeSchema.nullable(),
  committedAt: IsoDateTimeSchema.nullable(),
});
export type ImportJobListItem = z.infer<typeof ImportJobListItemSchema>;

export const ImportRowItemSchema = z.object({
  id: UuidSchema,
  /** Sheet within the source XLSX. Empty string for single-sheet imports. */
  sheetKey: z.string().default(''),
  rowIndex: z.number().int(),
  /** Original cells, string-keyed by column field name. */
  rawData: z.record(z.string(), z.unknown()),
  /** Operator's corrections - sparse; only fields the operator edited. */
  fixedData: z.record(z.string(), z.unknown()).nullable(),
  /** Field-level errors after the last validate() pass. NULL when VALID. */
  validationErrors: z.array(ImportFieldErrorSchema).nullable(),
  status: ImportRowStatusSchema,
  /** FK to the entity (Lead.id) when status=COMMITTED. */
  entityId: UuidSchema.nullable(),
  errorMessage: z.string().nullable(),
});
export type ImportRowItem = z.infer<typeof ImportRowItemSchema>;

export const ImportJobDetailSchema = ImportJobListItemSchema.extend({
  // Empty for now - rows are fetched separately with pagination + status
  // filter so the wizard can stream through large jobs without one giant
  // response. We keep the field here as a marker for future denorm.
});
export type ImportJobDetail = z.infer<typeof ImportJobDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const ImportRowListQuerySchema = PaginationQuerySchema.extend({
  status: ImportRowStatusSchema.optional(),
  /** Filter rows to a single sheet (multi-sheet importers only). */
  sheetKey: z.string().max(40).optional(),
});
export type ImportRowListQuery = z.infer<typeof ImportRowListQuerySchema>;

export const ImportRowListResponseSchema = paginatedResponseSchema(ImportRowItemSchema);
export type ImportRowListResponse = z.infer<typeof ImportRowListResponseSchema>;

export const ImportJobListQuerySchema = PaginationQuerySchema.extend({
  moduleKey: ImportModuleKeySchema.optional(),
  status: ImportJobStatusSchema.optional(),
});
export type ImportJobListQuery = z.infer<typeof ImportJobListQuerySchema>;

export const ImportJobListResponseSchema = paginatedResponseSchema(ImportJobListItemSchema);
export type ImportJobListResponse = z.infer<typeof ImportJobListResponseSchema>;

/**
 * Body of `PATCH /imports/:jobId/rows/:rowIndex` - sparse map of column
 * field name → new value. Sending `null` clears the fix for that field
 * (i.e. revert to the raw value).
 */
export const ImportRowPatchRequestSchema = z.object({
  fixedCells: z.record(z.string(), z.unknown()),
});
export type ImportRowPatchRequest = z.infer<typeof ImportRowPatchRequestSchema>;

/**
 * Body of `POST /imports/:jobId/rows/:rowIndex/skip` - operator drops the
 * row before commit so it doesn't get imported but its raw data is kept
 * for the audit log.
 */
export const ImportRowSkipRequestSchema = z.object({
  skip: z.boolean(),
});
export type ImportRowSkipRequest = z.infer<typeof ImportRowSkipRequestSchema>;

/**
 * `GET /imports/:moduleKey/template` response. Returns a starter XLSX as
 * a base64 blob. The admin client decodes it and triggers a browser
 * download so operators can grab a sample file without running any
 * dev-side script.
 */
export const ImportTemplateSchema = z.object({
  moduleKey: ImportModuleKeySchema,
  filename: z.string(),
  contentBase64: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type ImportTemplate = z.infer<typeof ImportTemplateSchema>;

/**
 * `GET /imports/:moduleKey/guide` response. Returns the per-entity
 * operator guide (Markdown) as a base64 blob. The admin client decodes
 * it and triggers a browser download (.md). Source files live at
 * `docs/operator-guides/bulk-imports/<moduleKey>.md`.
 */
export const ImportGuideSchema = z.object({
  moduleKey: ImportModuleKeySchema,
  filename: z.string(),
  contentBase64: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type ImportGuide = z.infer<typeof ImportGuideSchema>;

/**
 * `GET /imports/jobs/:jobId/source-file` response. The API gives a
 * short-lived presigned S3 URL the operator can download the original
 * XLSX from for re-validation / audit / re-upload.
 */
export const ImportSourceFileSchema = z.object({
  jobId: UuidSchema,
  fileName: z.string(),
  fileSizeBytes: z.number().int(),
  fileSha256: z.string().nullable(),
  /** Null when the file has been purged by the retention sweep. */
  url: z.string().url().nullable(),
  /** ISO timestamp; null when `url` is null. */
  urlExpiresAt: IsoDateTimeSchema.nullable(),
  purgedAt: IsoDateTimeSchema.nullable(),
});
export type ImportSourceFile = z.infer<typeof ImportSourceFileSchema>;
